import { TASK_STATUSES, type TaskStatus } from '@/types/task';

/**
 * How each status is written and coloured.
 *
 * The stored names are the shared `assignment.status` values, which is why
 * PENDING reads as "Yet to start" and CANCELLED as "Dismissed": the column
 * predates the board and is also written by dispatch runs.
 *
 * `ink` colours the status glyph alone (artifact `.st-ci`): a status is a
 * system indicator, not a tag — no pill, no background, no border — so the
 * semantic colour lives on the icon and the label stays in the board's own
 * ink. Yet-to-start and Dismissed share the neutral grey (neither asks for
 * attention); the two live states borrow the update types' own colours; only
 * the terminal success state earns a solid disc.
 */
export const STATUS_META: Record<TaskStatus, { label: string; ink: string }> = {
  PENDING: { label: 'Yet to start', ink: '#9A9A9A' },
  IN_PROGRESS: { label: 'In progress', ink: '#EDC22B' },
  IN_REVIEW: { label: 'In review', ink: '#F56B10' },
  COMPLETED: { label: 'Done', ink: '#166534' },
  CANCELLED: { label: 'Dismissed', ink: '#9A9A9A' },
};

/** Pipeline order, which is what a sort on status means to a reader. */
export const STATUS_ORDER = [ ...TASK_STATUSES ];

/** The statuses a task is finished in, where "Mark as Done" has nothing left to offer. */
export const CLOSED_STATUSES: TaskStatus[] = [ 'COMPLETED', 'CANCELLED' ];

export const statusLabel = (status: TaskStatus) => STATUS_META[status]?.label ?? status;
